import { ChangeDetectorRef, Component, HostBinding, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICustomerImage } from '../../models/customer-image.interface';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { UtilityService } from '../../../../services/utility/utility.service';
import { IPaperlessCommercialInvoiceEnrollment } from '../../models/paperless-commercial-invoice-enrollment.interface';
import { DialogService } from '../../../../services/dialog/dialog.service';
import { PaperlessCommercialInvoiceService } from '../../services/paperless-commercial-invoice.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'upsc-paperless-commercial-invoice-settings-dialog',
  templateUrl: './paperless-commercial-invoice-settings-dialog.component.html',
  styleUrls: ['./paperless-commercial-invoice-settings-dialog.component.scss'],
})
export class PaperlessCommercialInvoiceSettingsDialogComponent implements OnInit, OnDestroy {
  @HostBinding('class') public hostClass = 'paperless-commercial-invoice-settings-dialog';

  public letterheadImage: ICustomerImage;
  public signatureImage: ICustomerImage;
  public formGroup: UntypedFormGroup;
  public isEdit = false;

  public showFileSizeExceededError: boolean = false;
  private fileSizeLimit = 100000; // 102400;

  private readonly originalLetterheadImageId: number;
  private readonly originalSignatureImageId: number;

  private deleteCustomerDocumentSubscription: Subscription;
  private deleteCustomerDocumentsSubscription: Subscription;
  private enrollPaperlessCommercialInvoiceSubscription: Subscription;
  private uploadCustomerDocumentsSubscription: Subscription;
  public uploadCustomerDocumentsErrorMessage: string;
  public letterHeadAndSignatureErrorMessage: string;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private paperlessCommercialInvoiceService: PaperlessCommercialInvoiceService,
    private utilityService: UtilityService,
    private cdRef: ChangeDetectorRef,
    private dialogService: DialogService,
    public dialogRef: MatDialogRef<PaperlessCommercialInvoiceSettingsDialogComponent>,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any) {
    if (data) {
      this.letterheadImage = this.data.letterhead;
      this.signatureImage = this.data.signature;
    }

    this.isEdit = !!this.letterheadImage && !!this.signatureImage;

    this.initForm();
    if (this.letterheadImage) {
      this.originalLetterheadImageId = this.letterheadImage.LinkId;
      this.formGroup.controls.letterhead.setValue(this.letterheadImage.Document);
    }

    if (this.signatureImage) {
      this.originalSignatureImageId = this.signatureImage.LinkId;
      this.formGroup.controls.signature.setValue(this.signatureImage.Document);
    }
  }

  public ngOnInit() {
    this.checkIfLetterHeadAndSignatureHasErrorMessages();
  }

  public ngOnDestroy() {
    this.utilityService.clearSubscriptions([
      this.deleteCustomerDocumentSubscription,
      this.deleteCustomerDocumentsSubscription,
      this.enrollPaperlessCommercialInvoiceSubscription,
      this.uploadCustomerDocumentsSubscription,
    ]);
    this.resetBehaviorSubjects();
  }

  private initForm() {
    this.formGroup = this.formBuilder.group({
      letterhead: [null, Validators.compose([Validators.required])],
      signature: [null, Validators.compose([Validators.required])],
    });
  }

  public closeDialog(event, result) {
    event.preventDefault();

    this.dialogRef.close(result);
  }

  public uploadImage(files: FileList, type: string) {
    
    if (!files || files.length === 0) {
      return;
    }

    const reader = new FileReader();
    const file = files[0];
    if (file.size > this.fileSizeLimit) {      
      this.showFileSizeExceededError = true;
      return;
    }

    this.showFileSizeExceededError = false;

    reader.readAsDataURL(file);

    reader.onload = () => {
      // TODO: Need to test below statement
      const image = (<string>reader.result).split(',')[1];

      switch (type) {
        case 'letterhead':
          this.formGroup.controls.letterhead.setValue(image);
          this.letterheadImage = {
            FileType: '2',
            Document: image,
            DocumentType: file.type.split('/')[1] || file.type.split('/')[0] || file.type,
          };

          break;
        case 'signature':
          this.formGroup.controls.signature.setValue(image);
          this.signatureImage = {
            FileType: '1',
            Document: image,
            DocumentType: file.type.split('/')[1] || file.type.split('/')[0] || file.type,
          };

          break;
        default:
          break;
      }

      // need to run CD since file load runs outside of zone
      this.cdRef.markForCheck();
    };
  }

  public removeImage(event, type: string, isDisabled: boolean) {
    event.preventDefault();

    if (isDisabled) {
      return;
    }

    this.dialogService.confirm(
      '',
      this.translateService.instant('paperlessCommercialInvoice.dialog.settings.confirmRemove'),
      '',
      this.translateService.instant('paperlessCommercialInvoice.dialog.settings.confirm'),
      this.translateService.instant('paperlessCommercialInvoice.dialog.settings.cancel'),
      'alternate',
    ).subscribe(
      (result) => {
        if (!result) {
          return;
        }

        switch (type) {
          case 'letterhead':
            this.removeLetterhead();
            break;
          case 'signature':
            this.removeSignature();
            break;
          default:
            break;
        }
      },
    );
  }

  private removeLetterhead() {
    this.letterheadImage = null;
    this.formGroup.controls.letterhead.setValue(null);
  }

  private removeSignature() {
    this.signatureImage = null;
    this.formGroup.controls.signature.setValue(null);
  }

  /**
   * Use this if not all images are required.
   * (A user is allowed to upload just some image.)
   */
  public saveImages(event) {
    event.preventDefault();

    if (!this.letterheadImage || !this.letterheadImage.LinkId) {
      this.utilityService.clearSubscriptions([this.deleteCustomerDocumentSubscription]);
      this.deleteCustomerDocumentSubscription =
        this.paperlessCommercialInvoiceService.deleteCustomerDocument(this.originalLetterheadImageId).subscribe();
    }

    if (!this.signatureImage || !this.signatureImage.LinkId) {
      this.utilityService.clearSubscriptions([this.deleteCustomerDocumentSubscription]);
      this.deleteCustomerDocumentSubscription =
        this.paperlessCommercialInvoiceService.deleteCustomerDocument(this.originalSignatureImageId).subscribe();
    }

    if (!this.letterheadImage && !this.signatureImage) {
      return;
    }

    const data: IPaperlessCommercialInvoiceEnrollment = {
      termsId: '1000',
    };

    this.utilityService.clearSubscriptions([this.enrollPaperlessCommercialInvoiceSubscription]);
    this.enrollPaperlessCommercialInvoiceSubscription =
      this.paperlessCommercialInvoiceService.enrollPaperlessCommercialInvoice(data).subscribe(
        result => this.handleEnrollPaperlessCommercialInvoiceSuccess(result),
      );
  }

  /**
   * All images are required.
   */
  public saveAllImages(event) {
    event.preventDefault();

    const data: IPaperlessCommercialInvoiceEnrollment = {
      termsId: '1000',
    };

    this.utilityService.clearSubscriptions([this.enrollPaperlessCommercialInvoiceSubscription]);
    this.enrollPaperlessCommercialInvoiceSubscription =
      this.paperlessCommercialInvoiceService.enrollPaperlessCommercialInvoice(data).subscribe(
        result => this.handleEnrollPaperlessCommercialInvoiceSuccess(result),
      );
  }

  private handleEnrollPaperlessCommercialInvoiceSuccess(enrollmentResult) {
    const data: ICustomerImage[] = [];
    const isLetterheadImageChanged = !this.originalLetterheadImageId || this.letterheadImage.LinkId !== this.originalLetterheadImageId;
    if (this.letterheadImage && isLetterheadImageChanged) {
      data.push(this.letterheadImage);
    }

    const isSignatureImageChanged = !this.originalSignatureImageId || this.signatureImage.LinkId !== this.originalSignatureImageId;
    if (this.signatureImage && isSignatureImageChanged) {
      data.push(this.signatureImage);
    }

    if (!data.length) {
      // no change made to the iamges.
      this.dialogRef.close();
      return;
    }

    this.setDefaultCommercialInvoiceTypeIfEnrolledSuccess();
    
    this.uploadCustomerDocumentsSubscription = this.paperlessCommercialInvoiceService.uploadCustomerDocuments(data).subscribe(
      result => this.handleUploadCustomerDocumentsSuccess(result),
      error => this.handleUploadCustomerDocumentsError(error)
    );
  }

  public setDefaultCommercialInvoiceTypeIfEnrolledSuccess(): void {
    this.paperlessCommercialInvoiceService.updateCustomerCommercialInvoiceType(3).subscribe(
      res => {
        this.paperlessCommercialInvoiceService.isEnrolledClickedPaperlessCommercialInvoice$.next(res);
      }
    )
  }

  public handleUploadCustomerDocumentsError(error) {
    this.paperlessCommercialInvoiceService.uploadCustomerDocumentsErrorMessage$.next(error.error.Message);
  }

  private handleUploadCustomerDocumentsSuccess(result) {
    this.dialogRef.close();
  }

  public unenroll(event) {
    event.preventDefault();

    // this.utilityService.clearSubscriptions([this.deleteCustomerDocumentSubscription]);
    // this.deleteCustomerDocumentSubscription =
    //   this.companyProfileService.deleteCustomerDocument(this.originalLetterheadImageId).subscribe();
    //
    // this.utilityService.clearSubscriptions([this.deleteCustomerDocumentSubscription]);
    // this.deleteCustomerDocumentSubscription =
    //   this.companyProfileService.deleteCustomerDocument(this.originalSignatureImageId).subscribe();

    this.utilityService.clearSubscriptions([this.deleteCustomerDocumentsSubscription]);
    const data: { LinkID: number }[] = [];
    if (this.originalLetterheadImageId) {
      data.push({ LinkID: this.originalLetterheadImageId });
    }

    if (this.originalSignatureImageId) {
      data.push({ LinkID: this.originalSignatureImageId });
    }

    this.deleteCustomerDocumentsSubscription = this.paperlessCommercialInvoiceService.deleteCustomerDocuments(data).subscribe(
      result => this.handleDeleteCustomerDocumentsSuccess(result),
    );
  }

  private handleDeleteCustomerDocumentsSuccess(result) {
    this.dialogRef.close();
  }

  public checkIfLetterHeadAndSignatureHasErrorMessages(): void {
    this.paperlessCommercialInvoiceService.uploadCustomerDocumentsLetterHeadAndSignatureErrorMessage$.subscribe(
      (result) => {
        this.letterHeadAndSignatureErrorMessage = result;
      }
    )
  }

  public resetBehaviorSubjects(): void {
    this.paperlessCommercialInvoiceService.uploadCustomerDocumentsErrorMessage$.next(null);
    this.paperlessCommercialInvoiceService.uploadCustomerDocumentsLetterHeadAndSignatureErrorMessage$.next(null);
  }
}
